<template>
  <div class="">
    <BaseTable class="no-border exam-table" v-if="table_exam" :tabledata="table_exam"
      @item-opened="$emit('item-opened')" v-bind="$attrs">
      <template v-slot:column_name="{ item }">
        <div class="patient-box list" :class="{ 'anonymous': isJokerPatient(item) }" :data-exam-id="item.id">
          <a href="#" @click.prevent.stop="isJokerPatient(item) ? $emit('do-assign', item) : toPatientDetails(item)">
            <span>{{ isJokerPatient(item) ? $t('noPatientSelected') : item.patient.fullName }}</span>
          </a>
        </div>

        <button class="btn btn-outline-secondary float-end" type="button" name="button" @click="$emit('item-opened')">
          {{ $t('user.exam') }}
          <IconArrowRight/>
        </button>
      </template>

      <template v-slot:column_captures="{ item }">
        <div class="preview-image-box" v-if="item.examImages.length" :data-title="$t('exam.iaText')">
          <ul class="preview-image-list">
            <template v-for="(image, index) in item.examImages">
              <li v-if="allowedImages(image.type) && index < 3">
                <img :src="thumb_path + '/' + image.name" alt="">
              </li>
            </template>
            <li class="counter" v-if="item.examImages.length > 2">+{{ item.examImages.length - 3 }}</li>
          </ul>
        </div>

        <div class="no-captures" v-else>
          <p>{{ $t('exam.noCaptures') }}</p>
        </div>
      </template>

      <template v-slot:column_content="{ item }">
        <div class="d-inline-block">
          <span v-show="item.examType.key == 'patient_progression'" class="label label-blue-phelcom"
            :data-title="$t(`exam.examTypeName_${item.examType.key}`)">{{ $t(`exam.examType_${item.examType.key}`)
            }}</span>

          <i v-if="showMutirao && item.referred > 0" class="fa"
            :class="item.referred == 1 ? 'fa-tint' : 'fa-check-circle-o'" aria-hidden="true"></i>

          <template v-if="hasUnifiedReportFeature">
            <div class="btn-group btn-group-mark" :key="item.uuid" v-if="!item.unifiedExam?.type" :class="{ 'disabled': shouldDisableActionButtons(item) }">
              <p class="btn btn-outline-secondary" :data-test="`btnExamHasReport_${item.uuid}`">
                <IconLamp/>
                {{ $t('exam.examIs') }}
              </p>
              <button type="button" class="btn btn-outline-secondary" :class="{ 'disabled': shouldDisableActionButtons(item) }"
                 :data-test="`btnExamNoAlteration_${item.uuid}`" @click.stop.prevent="setAlteration('notAltered', item)" :disabled="shouldDisableActionButtons(item)">
                <IconCheckBig/>
                <span>{{ $t('exam.notAltered') }}</span>
              </button>
              <button type="button" class="btn btn-outline-secondary" :class="{ 'disabled': shouldDisableActionButtons(item) }"
                 :data-test="`btnExamHasAlteration_${item.uuid}`" @click.stop.prevent="setAlteration('altered', item)" :disabled="shouldDisableActionButtons(item)">
                <IconWarningCircle/>
                <span>{{ $t('exam.altered') }}</span>
              </button>
              <button type="button" class="btn btn-outline-secondary" :class="{ 'disabled': shouldDisableActionButtons(item) }"
                 :data-test="`btnExamNoQuality_${item.uuid}`" @click.stop.prevent="setAlteration('noQuality', item)" :disabled="shouldDisableActionButtons(item)">
                <IconCross/>
                <span>{{ $t('exam.noQuality') }}</span>
              </button>
            </div>

            <div class="report-info-box" v-else>
              <button class="btn btn-outline-secondary" :class="getAlterationClass(item.unifiedExam?.type)" type="button" name="button" v-if="!item.hasReport">
                <IconPdfExam/>
                {{ $t('tooltips.report') }}
                <span>{{ $t(`exam.${item.unifiedExam?.type}`) }}</span>
              </button>

              <div class="dropdown" v-else>
                <button class="btn btn-outline-secondary dropdown-toggle" :class="getAlterationClass(item.unifiedExam?.type)" type="button" data-bs-toggle="dropdown" aria-expanded="false" @click.prevent.stop="false">
                  <IconPdfExam/>
                  {{ $t('tooltips.report') }}
                  <span>{{ $t(`exam.${item.unifiedExam?.type}`) }}</span>
                </button>
                <ul class="dropdown-menu">
                  <li>
                    <a class="dropdown-item" :data-test="`btnExamEditReport_${item.uuid}`" @click="goToReport(item)">{{ $t('exam.editReport') }}</a>
                  </li>
                  <li>
                    <a class="dropdown-item" :data-test="`btnExamDownload_${item.uuid}`" @click.stop.prevent="doDownload(item)">{{ $t('exam.downloadExam') }}</a>
                  </li>
                  <li>
                    <a class="dropdown-item" :data-test="`btnExamHasReport_${item.uuid}`" @click.stop.prevent="doDownloadPDF(item)">{{ $t('exam.downloadReport') }}</a>
                  </li>
                </ul>
              </div>
            </div>

            <button class="btn btn-outline-secondary btn-share" v-if="!isSpecialist && !isJokerPatient(item)" type="button" name="button"
              data-test="btnPatientShare" @click.prevent.stop="shareInformation(item)">
              <IconShare />
            </button>
          </template>

          <template v-else>
            <button class="no-btn" type="button" name="button" data-title="Download"
              :data-test="`btnExamDownload_${item.uuid}`" @click.stop.prevent="doDownload(item)">
              <IconDownload />
            </button>
            <button class="no-btn" :disabled="!item.hasImage" type="button" name="button" :data-title="$t('tooltips.image')"
              :data-test="`btnExamHasImage_${item.uuid}`">
              <IconImage />
            </button>
            <button class="no-btn" :disabled="!item.hasReport || isJokerPatient(item)" type="button" name="button"
              :data-title="$t('tooltips.report')" :data-test="`btnExamHasReport_${item.uuid}`" @click.stop.prevent="doDownloadPDF(item)">
              <IconPdf />
            </button>
          </template>
        </div>
      </template>

      <template v-slot:column_ai="{ item }">
        <AIScoreResult :item="item" />
      </template>

    </BaseTable>

    <teleport to="body">
      <BasePatientAccessInfoOffcanvas ref="offcanvasPatientAccessInfo" />
    </teleport>
  </div>

</template>

<style lang="scss" scoped>
.fa {
  font-size:                           22px !important;
  display:                             flex;
  width:                               36px;
  align-items:                         center;
  justify-content:                     center;
}

.d-inline-block {
  display:                             flex !important;
  flex-direction:                      row;
  align-items:                         center;
  gap:                                 10px;
}

.preview-image-box {
  ul {
    display:                           flex;
    align-items:                       center;
    flex-direction:                    row;
    list-style-type:                   none;
    padding:                           0;
    margin:                            0;

    li {
      width:                           31px;
      height:                          26px;

      img {
        @include                       border_radius(99px);
        width:                         100%;
        height:                        100%;
        border:                        2px solid var(--Shades-W, #FFF);
      }

      &:not(:first-child) {
        margin-left:                   -5px;
      }

      &.counter {
        @include                       border_radius(99px);
        color:                         var(--Shades-60, #727272);
        text-align:                    center;
        font-size:                     12px;
        font-weight:                   700;
        line-height:                   2;
        letter-spacing:                0.6px;
        border:                        1px solid var(--Shades-40, #B7B7B7);
        background:                    var(--Shades-W, #FFF);
      }
    }
  }
}

.btn-share {
  font-size:                           var(--font-size-10);
  display:                             flex;
  flex-direction:                      row;
  gap:                                 5px;
  height:                              32px;
  padding:                             6px 16px;
  // margin-left:                         10px;
}

@media (max-width: 991px) {
  .exam-table .btn-group-mark p.btn.btn-outline-secondary {
    background:                        unset !important;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import AIScoreResult from '../ai/AIScoreResult.vue'


export default {
  name: 'ExamTableList',
  props: ['table_exam', 'thumb_path'],
  components: {
    AIScoreResult
  },
  data: () => ({
    session: {
      collaborator: {},
      currentClinic: {},
      showMutirao: false,
    },
    hasUnifiedReportFeature: false,
    isSpecialist: null,
  }),
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
    this.showMutirao = this.session.features.includes('mutirao')
    this.hasUnifiedReportFeature = this.session.features.includes('unifiedReport')
    this.isSpecialist = this.session.isSpecialist
  },
  methods: {
    doDownload(exam) {
      try {
        NProgress.start()
        this.$examService.download(exam.id)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
      NProgress.done(true)
    },
    toPatientDetails(item) {
      this.$router.push({ name: 'patient-show', params: { patientID: item.patient.id }, query: { tab: 'data' } })
    },
    isJokerPatient(item) {
      return item.patient.uuid == this.session.currentClinic.jokerPatientUuid
    },
    shouldDisableActionButtons(item) {
      return this.isJokerPatient(item) || !item.hasImage
    },
    allowedImages(image) {
      // return image.endsWith('COLOR') ||
      //   image.endsWith('REDFREE') ||
      //   image.endsWith('redfree') ||
      //   image.endsWith('TEXTURE') ||
      //   image.endsWith('HEATMAP') ||
      //   image.endsWith('heatmap') ||
      //   image.endsWith('image') &&
      return !image.endsWith('VIDEO')
    },
    async doDownloadPDF(item){
      console.log('item: ', item)
      let params = {
        id: item.id,
      }
      try {
        let response = await this.$reportService.getList(params);

        if (response.result.length > 0) {
          let theReport = response.result[response.result.length - 1]
          let params = {
            id: theReport.id
          }
          this.$reportService.generateReport(params);
        }

      } catch(err) {
         this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }

    },
    async setAlteration(value, item) {
      let params = {
        examId: item.id,
        type: value
      }

      this.loading = true
      NProgress.start()
      try {
        let response = await this.$examService.updateUnifiedExam(params)
        console.log('response setAlteration', response);

        response.exam.unifiedExam.type == 'altered' ? this.goToReport(response.exam) : this.$emit('do-reload')
      } catch (e) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
        this.loading = false
      }

      console.log('item', item);
    },
    goToReport(item) {
      this.$router.push({ name: 'exam-show', params: { examID: item.id }, query: { tab: 'report', fromExamList: true } })
      // this.$router.push({ name: 'report', params: { examID: item.id, patient: item.patient } })
    },
    getAlterationClass(alteration) {
      if (typeof alteration === 'string') {
        if (alteration === 'altered') {
          return 'has-alteration'
        } else if (alteration === 'notAltered') {
          return 'no-alteration'
        } else if (alteration === 'noQuality') {
          return 'no-quality'
        }
      }
      return ''
    },
    async shareInformation(item) {
      const ok = await this.$refs.offcanvasPatientAccessInfo.show({
        patient: item.patient
      })
      if (ok) {
        NProgress.done(true)
      }
    },
  }
}
</script>
